import { C, H2, Lead, Note, P, Table } from '../Doc';

/**
 * Third-party components actually shipped, read from apps/studio/package.json and the
 * PackageReference entries in src/*.csproj. Licence names are the ones those projects
 * publish; the authority is each package's own LICENSE file, which is why the page says so
 * rather than presenting this table as legal advice.
 */
const NPM = [
  ['react, react-dom', '19.x', 'MIT'],
  ['react-router-dom', '7.x', 'MIT'],
  ['@tanstack/react-query', '5.x', 'MIT'],
  ['@dnd-kit/core, /sortable, /modifiers, /utilities', '6.x–10.x', 'MIT'],
  ['zustand', '5.x', 'MIT'],
  ['immer', '10.x', 'MIT'],
  ['clsx', '2.x', 'MIT'],
  ['qrcode', '1.5.x', 'MIT'],
  ['tailwindcss, @tailwindcss/vite', '4.x', 'MIT'],
  ['vite, @vitejs/plugin-react', '6.x / 4.x', 'MIT'],
  ['typescript', '5.7.x', 'Apache-2.0'],
];

const NUGET = [
  ['Microsoft.AspNetCore.* / Microsoft.Extensions.*', '10.0.11', 'MIT'],
  ['Microsoft.EntityFrameworkCore.SqlServer, .Design', '10.0.11', 'MIT'],
  ['Microsoft.Extensions.Http.Resilience', '10.9.0', 'MIT'],
  ['AWSSDK.S3', '4.0.102.5', 'Apache-2.0'],
  ['MailKit', '4.17.0', 'MIT'],
  ['ClosedXML', '0.104.2', 'MIT'],
  ['Konscious.Security.Cryptography.Argon2', '1.3.1', 'MIT'],
  ['WebPush', '1.0.13', 'MPL-2.0'],
  ['QuestPDF', '2026.8.0', 'Community / commercial — see note below'],
];

const FONTS = [
  ['Space Grotesk', 'SIL Open Font License 1.1', 'Loaded from Google Fonts'],
  ['JetBrains Mono', 'SIL Open Font License 1.1', 'Loaded from Google Fonts'],
];

export function Licensing() {
  return (
    <>
      <Lead>
        FormForge is proprietary software. This page states the licence that applies to it,
        and lists the third-party components it ships, which must be attributed regardless of
        how FormForge itself is licensed.
      </Lead>

      <H2 id="licence">Licence</H2>
      <div className="mt-4 max-w-[68ch] rounded-lg border border-[var(--ff-border)]
                      bg-[var(--ff-surface-raised)] p-4">
        <p className="font-semibold">FormForge — Proprietary. All rights reserved.</p>
        <p className="mt-2 leading-relaxed text-[var(--ff-text-muted)]">
          Copyright © 2026. This software and its source code are confidential and
          proprietary. No part of it may be copied, modified, distributed, sublicensed or
          used to create derivative works without express written permission from the
          copyright holder. No licence is granted by possession of, or access to, this
          software or its documentation.
        </p>
      </div>

      <Note tone="warn" title="One thing to complete">
        <P>
          The copyright line names a year but not a holder. Replace “Copyright © 2026” with
          the legal entity that owns the work — in this page, and in the{' '}
          <C>LICENSE</C> file at the repository root, which carries the full terms for
          anyone who receives the source without running the application.
        </P>
      </Note>

      <H2 id="third-party">Third-party components</H2>
      <P>
        Proprietary licensing does not remove the obligation to honour the licences of what
        is bundled. Every component below is shipped as part of the studio, the API or the
        embeddable runtime.
      </P>

      <H2 id="npm">Studio — npm</H2>
      <Table
        head={['Package', 'Version', 'Licence']}
        rows={NPM.map(([n, v, l]) => [<C key={n}>{n}</C>, v, l])}
      />

      <H2 id="nuget">Server — NuGet</H2>
      <Table
        head={['Package', 'Version', 'Licence']}
        rows={NUGET.map(([n, v, l]) => [<C key={n}>{n}</C>, v, l])}
      />

      <Note tone="danger" title="QuestPDF needs a decision">
        QuestPDF is dual-licensed. Its Community licence is free only below a revenue
        threshold; commercial use above it requires a paid licence. It is used to export a
        response as PDF. Confirm which applies to your organisation before shipping, or drop
        the dependency.
      </Note>

      <Note tone="warn" title="WebPush is MPL-2.0">
        Weak copyleft, and file-level: using the library in a proprietary product is fine,
        but modifications to its own files must be published. Do not vendor and patch it
        without checking that obligation.
      </Note>

      <H2 id="fonts">Fonts</H2>
      <Table
        head={['Font', 'Licence', 'Delivery']}
        rows={FONTS.map(([n, l, d]) => [n, l, d])}
      />
      <P>
        Both are open-licensed and free to embed. They are currently fetched from Google
        Fonts at runtime, which means the studio makes a request to a third party on load —
        self-host them if that is not acceptable in your environment.
      </P>

      <H2 id="runtime">The embeddable runtime</H2>
      <P>
        The runtime that partners host on their own pages is part of this proprietary work
        and carries no separate licence. If you intend partners to vendor, modify or
        redistribute it, that is a licensing decision to take deliberately — it is the one
        component designed to live inside somebody else's product.
      </P>

      <H2 id="accuracy">About this list</H2>
      <P>
        Package names and versions are taken from <C>apps/studio/package.json</C> and the{' '}
        <C>PackageReference</C> entries in <C>src/*.csproj</C>. Licence names are the ones
        those projects publish. This page is a summary for engineers, not legal advice, and
        it does not update itself — re-check it when dependencies change, and have counsel
        review the terms above before they are relied on commercially.
      </P>
    </>
  );
}
